/*
 * decaffeinate suggestions:
 * DS104: Avoid inline assignments
 * DS204: Change includes calls to have a more natural evaluation order
 * DS206: Consider reworking classes to avoid initClass
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import {Spec} from 'immutability-helper';
import React from 'react';

import EntryActions from 'actions/EntryActions';
import {ArchiveRecord} from 'archive/ArchiveConstants';
import {Permissions} from 'auth/PermissionConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {User} from 'constants/UserConstants';
import {Tag} from 'folder-items/FileConstants';
import ProxyFile, {ProxyFileAttributes} from 'folder-items/ProxyFile';
import {DocumentBanner} from 'notebook/components/banners/DocumentBanner';
import {EntryProtocol, ExternalProtocol, FromServerProtocol} from 'notebook/ELNConstants';
import {Entry as EntryStruct} from 'notebook/ELNConstants';
import {canEditEntry} from 'notebook/entries/canEditEntry';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import ReviewConstants, {Review} from 'notebook/reviews/ReviewConstants';
import {ItemEditorState} from 'notebook/store/VersionedItemSubstore';
import {EntryNote, NotePart} from 'notebook/util/NoteUtilConstants';
import EntryStore from 'stores/EntryStore';

interface EntryAttributes extends ProxyFileAttributes {
  collaboratorIds?: Array<string>;
}

export interface Entry {
  get<K extends keyof EntryAttributes>(key: K): EntryAttributes[K];
}

export class Entry extends ProxyFile<EntryStruct, typeof EntryStore> {
  static override initClass(): void {
    this.prototype.proxiedField = 'entry';
  }

  getStore(): typeof EntryStore {
    return EntryStore;
  }

  entryProtocols(): Array<EntryProtocol> {
    return this.proxied().entryProtocols;
  }

  override getEntryNote(): EntryNote {
    return EntryStore.getNote(this.id);
  }

  collaboratorIds(): Array<string> | undefined {
    return this.get('collaboratorIds');
  }

  templateVersionId(): number | undefined {
    return this.proxied().templateVersionId;
  }

  templateUrl(): string | undefined {
    return this.proxied().templateUrl;
  }

  templateVersionTimestamp(): string | undefined {
    return this.proxied().templateVersionTimestamp;
  }

  isStale(): boolean | undefined {
    return this.proxied().isStale;
  }

  override allowEdits(): boolean {
    return canEditEntry({
      entry: this.proxied(),
      isLatest: this.isLatest(),
      dirty: this.isDirty(),
    });
  }

  getState(): ItemEditorState<EntryStruct, NotePart, 'entry', 'entryId'> {
    return this.getStore().getEditorState(this.id)!;
  }

  openLatestVersion(): void {
    EntryActions.open(this.id);
  }

  // TODO T3917: refactor notifications and setBanner to use same text
  override setBanner(): void {
    if (this.review() && ReviewConstants.LOCKED_STATUSES.includes(this.review()!.status)) {
      this.setBannerText(
        <DocumentBanner.ReviewLocked
          status={this.review()!.status}
          docType={ModelTypeConstants.ENTRY}
          isLatest={this.isLatest()}
          onNavigateToLatest={() => {
            this.openLatestVersion();
          }}
        />
      );
    } else if (this.lockedFromExternalChange()) {
      this.setBannerText(<DocumentBanner.ExternalChangeLocked isUnsaved={this.isUnsaved()} />);
    } else if (!this.isLatest() || this.isDirty()) {
      this.setBannerText(
        <DocumentBanner.PreviousVersion
          canEditLatestVersion={this.writable()}
          docType={ModelTypeConstants.ENTRY}
          isEvaluationPersisted={Boolean(this.proxied()?.note?.noteRuntimeSnapshot?.isEvaluationPersisted)}
          onNavigateToLatest={() => {
            this.openLatestVersion();
          }}
        />
      );
    } else if (!this.writable()) {
      if (this.isArchived()) {
        this.setBannerText(<DocumentBanner.Archived docType={ModelTypeConstants.ENTRY} />);
      } else {
        this.setBannerText(<DocumentBanner.ReadOnly docType={ModelTypeConstants.ENTRY} />);
      }
    } else {
      this.setBannerText(null);
    }
  }

  override load(param: {versionId?: number | null; deferred?: JQueryDeferred<void>} = {}): void {
    const {versionId, deferred} = param;
    if (this.proxied() != null && versionId != null) {
      // If it's already loaded and they are trying to load a version
      EntryActions.loadVersion(this.id, versionId, {deferred});
    } else {
      // Otherwise, trigger a fresh open.
      EntryActions.open(this.id, {versionId, deferred});
    }
  }

  review(): Review | undefined {
    return this.proxied().review;
  }

  addProtocol(protocol: ExternalProtocol | FromServerProtocol): void {
    EntryActions.addProtocol(this.id, this.proxied(), protocol);
  }

  updateProtocol(protocolCid: string, op: Spec<EntryProtocol>): void {
    EntryActions.updateProtocol(this.id, this.proxied(), protocolCid, op);
  }

  removeProtocol(protocolCid: string): void {
    EntryActions.removeProtocol(this.id, this.proxied(), protocolCid);
  }

  revertProtocol(protocol: EntryProtocol, selectionData: SelectionRestoreData): void {
    EntryActions.revertProtocol(this.id, this.proxied(), protocol, selectionData);
  }

  reorderProtocols(cids: Array<string>): void {
    EntryActions.reorderProtocols(this.id, this.proxied(), cids);
  }

  override setTagSchema(tagSchema: TagSchema): void {
    EntryActions.postServerUpdate(this.id, {tagSchema});
  }

  override setTags(tags: Array<Tag>): void {
    // @ts-expect-error `postServerUpdate` only accepts `SchemaTag`, but `ProxyFile.setTags` is too wide by
    // using regular `Tag`
    EntryActions.postServerUpdate(this.id, {tags});
  }

  override setAuthors(authors: Array<User>): void {
    EntryActions.postServerUpdate(this.id, {authors});
  }

  override setArchiveRecord(archiveRecord: ArchiveRecord | null): void {
    EntryActions.postServerUpdate(this.id, {archiveRecord});
  }

  override setPermissions(permissions: Permissions): void {
    EntryActions.postServerUpdate(this.id, {permissions});
  }

  override cleanUp(): void {
    super.cleanUp();
    EntryActions.close(this.id);
  }
}
Entry.initClass();
